"use client";

import { startTransition, useEffect, useRef, useState } from "react";

type UseScrollProps = {
  threshold: number;
  debounceDelay?: number;
};

const useScroll = ({ threshold, debounceDelay = 100 }: UseScrollProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollStateRef = useRef<boolean>(false);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      startTransition(() => {
        const hasScrolledPastThreshold = currentScrollY > threshold;

        if (hasScrolledPastThreshold !== scrollStateRef.current) {
          scrollStateRef.current = hasScrolledPastThreshold;
          setIsScrolled(hasScrolledPastThreshold);
        }

        setScrollPosition(currentScrollY);
      });
    }, debounceDelay);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [threshold, debounceDelay]);

  return { isScrolled, scrollPosition } as const;
};

export default useScroll;